import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { propertyTermsService } from 'api/services/propertyTermsService';
import { propertyService } from 'api/services/propertyService';
import type { PropertyTerms, CreatePropertyTermsRequest, UpdatePropertyTermsRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PropertyTermsModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: PropertyTerms | null;
  onSave: () => void;
  onClose: () => void;
  // Dynamic term type options from Config framework
  termTypeOptions?: SelectOption[];
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordId?: string) => void;
}

interface PropertyTermsFormData {
  propertyId: string;
  termType: string;
  languageCode: string;
  termsText: string;
  validFrom: string;
  validTo: string;
  isActive: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: PropertyTermsFormData = {
  propertyId: '',
  termType: 'BOOKING_CONFIRMATION',
  languageCode: 'en',
  termsText: '',
  validFrom: '',
  validTo: '',
  isActive: true,
};

const languageOptions: SelectOption[] = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const PropertyTermsModal: React.FC<PropertyTermsModalProps> = ({
  isOpen, mode, record, onSave, onClose, termTypeOptions = [],
  tabId, moduleName, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<PropertyTermsFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Property options for dropdown ---
  const [propertyOptions, setPropertyOptions] = useState<SelectOption[]>([]);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<PropertyTerms | null | undefined>(record);

  // --- Load property options ---
  useEffect(() => {
    if (!isOpen) return;
    const loadProperties = async () => {
      try {
        const res = await propertyService.getAll({ pageSize: 500, includeInactive: false });
        setPropertyOptions(
          res.data.map(p => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }))
        );
      } catch { /* ignore */ }
    };
    loadProperties();
  }, [isOpen]);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    let data: PropertyTermsFormData;
    if ((mode === 'edit' || mode === 'copy') && rec) {
      data = {
        propertyId: rec.propertyId || '',
        termType: rec.termType || 'BOOKING_CONFIRMATION',
        languageCode: rec.languageCode || 'en',
        termsText: rec.termsText || '',
        validFrom: rec.validFrom ? rec.validFrom.substring(0, 10) : '',
        validTo: rec.validTo ? rec.validTo.substring(0, 10) : '',
        isActive: rec.isActive,
      };
    } else {
      data = { ...INITIAL_FORM_DATA };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    const termTypeLabel = termTypeOptions.find(o => o.value === currentFormData.termType)?.label || currentFormData.termType;
    let title: string;
    if (currentMode === 'create') {
      const idx = currentCreateInstanceId || '1';
      title = termTypeLabel ? `Add - ${termTypeLabel} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const idx = currentCopyInstanceId || '1';
      title = termTypeLabel ? `Copy - ${termTypeLabel} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${termTypeLabel || 'Property Terms'}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? recordRef.current?.id : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.propertyId) {
      errors.propertyId = 'Property is required';
    }

    if (!formData.termType) {
      errors.termType = 'Term Type is required';
    }

    if (!formData.termsText.trim()) {
      errors.termsText = 'Terms Text is required';
    }

    if (formData.validFrom && formData.validTo) {
      if (new Date(formData.validFrom) > new Date(formData.validTo)) {
        errors.validTo = 'Valid To must be after Valid From';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input,textarea')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save (3-stage: confirm → loading → toast) ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreatePropertyTermsRequest | UpdatePropertyTermsRequest = {
        propertyId: formData.propertyId,
        termType: formData.termType,
        languageCode: formData.languageCode,
        termsText: formData.termsText.trim(),
        validFrom: formData.validFrom || undefined,
        validTo: formData.validTo || undefined,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await propertyTermsService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdatePropertyTermsRequest);
      } else {
        await propertyTermsService.create(payload as CreatePropertyTermsRequest);
      }

      setIsSaving(false);
      const termTypeLabel = termTypeOptions.find(o => o.value === formData.termType)?.label || formData.termType;
      notificationService.toast.success(
        `"${termTypeLabel}" terms ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh record from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await propertyTermsService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data: PropertyTermsFormData = {
          propertyId: rec.propertyId || '',
          termType: rec.termType || 'BOOKING_CONFIRMATION',
          languageCode: rec.languageCode || 'en',
          termsText: rec.termsText || '',
          validFrom: rec.validFrom ? rec.validFrom.substring(0, 10) : '',
          validTo: rec.validTo ? rec.validTo.substring(0, 10) : '',
          isActive: rec.isActive,
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {/* Loading overlay */}
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem'
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'edit' ? 'Saving...' : 'Creating...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Property Terms' : mode === 'copy' ? 'Copy Property Terms' : 'Edit Property Terms'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Property & Term Type --- */}
          <div className="form-grid-3">
            <div className="form-group" data-field="propertyId">
              <SyutSelect
                label={<>Property <span className="text-danger">*</span></>}
                options={propertyOptions}
                value={formData.propertyId}
                onChange={(val) => { clearFieldError('propertyId'); setFormData(prev => ({ ...prev, propertyId: (val as string) || '' })); }}
                placeholder="Select property..."
                isClearable
                size="md"
                error={getFieldError('propertyId')}
              />
            </div>

            <div className="form-group" data-field="termType">
              <SyutSelect
                label={<>Term Type <span className="text-danger">*</span></>}
                options={termTypeOptions}
                value={formData.termType}
                onChange={(val) => { clearFieldError('termType'); setFormData(prev => ({ ...prev, termType: (val as string) || '' })); }}
                placeholder="Select type..."
                isClearable={false}
                size="md"
                error={getFieldError('termType')}
              />
            </div>

            <div className="form-group" data-field="languageCode">
              <SyutSelect
                label="Language"
                options={languageOptions}
                value={formData.languageCode}
                onChange={(val) => setFormData(prev => ({ ...prev, languageCode: (val as string) || 'en' }))}
                placeholder="Select language..."
                isClearable={false}
                size="md"
              />
            </div>
          </div>

          {/* --- Terms Text --- */}
          <div className="form-group" data-field="termsText">
            <label className="form-label">Terms Text <span className="text-danger">*</span></label>
            <textarea
              className={`form-input${getFieldError('termsText') ? ' form-field-error' : ''}`}
              value={formData.termsText}
              onChange={(e) => { clearFieldError('termsText'); setFormData(prev => ({ ...prev, termsText: e.target.value })); }}
              placeholder="Enter terms and policies... (each line becomes a bullet point in the PDF)"
              rows={10}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
            {getFieldError('termsText') && <div className="form-error-message">{getFieldError('termsText')}</div>}
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Each line will appear as a separate bullet point in the booking confirmation PDF.
            </div>
          </div>

          {/* --- Valid From / Valid To / Status --- */}
          <div className="form-grid-3">
            <div className="form-group" data-field="validFrom">
              <label className="form-label">Valid From</label>
              <input
                type="date"
                className="form-input"
                value={formData.validFrom}
                onChange={(e) => setFormData(prev => ({ ...prev, validFrom: e.target.value }))}
              />
            </div>

            <div className="form-group" data-field="validTo">
              <label className="form-label">Valid To</label>
              <input
                type="date"
                className={`form-input${getFieldError('validTo') ? ' form-field-error' : ''}`}
                value={formData.validTo}
                onChange={(e) => { clearFieldError('validTo'); setFormData(prev => ({ ...prev, validTo: e.target.value })); }}
              />
              {getFieldError('validTo') && <div className="form-error-message">{getFieldError('validTo')}</div>}
            </div>

            <div className="form-group" data-field="isActive">
              <SyutSelect
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                value={String(formData.isActive)}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: val === 'true' }))}
                isClearable={false}
                size="md"
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Property Terms'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the property terms?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default PropertyTermsModal;
